/**
 * Dictate a question (UC-A6).
 *
 * The case this exists for: an agent on a headset with a customer on the line, typing one-handed.
 * Speaking the question is the difference between using the assistant during a call and after it.
 *
 * ## It fills the composer; it does not send
 *
 * The employee reads what was heard and corrects it before asking. A mis-transcribed account number
 * that goes straight to retrieval produces a wrong answer with a plausible citation, and nobody
 * ever sees what was actually asked. ADR 0008 §5.
 *
 * ## Recording is visible while it happens
 *
 * The button changes shape, a timer counts, and Escape cancels. A control that looks the same
 * whether or not the microphone is live is the one that makes people distrust the feature — and
 * they are right to.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MicIcon,
  StopIcon,
  cx,
  useDictation,
  useLanguage,
  useSpeechStatus,
} from "@nab/shared";

export interface DictateButtonProps {
  /** Called with the words. The composer fills; nothing is sent. */
  onTranscript: (text: string) => void;
  /** The conversation, so a dictated question is masked in the same scope as a typed one. */
  sessionId?: string | null;
  disabled?: boolean;
}

export function DictateButton({
  onTranscript,
  sessionId,
  disabled = false,
}: DictateButtonProps): JSX.Element | null {
  const { t } = useTranslation("app");
  const status = useSpeechStatus();
  const { language } = useLanguage();
  const [elapsed, setElapsed] = useState(0);

  const dictation = useDictation({
    onTranscript: (heard) => onTranscript(heard.text),
    maxSeconds: status.max_audio_seconds,
    // A hint, not an instruction. An agent whose interface is English may well dictate in Arabic,
    // and the engine's own detection is better than the interface's guess.
    language,
    ...(sessionId ? { sessionId } : {}),
    messages: {
      denied: t("speech.micDenied"),
      unsupported: t("speech.micUnsupported"),
      failed: t("speech.dictateFailed"),
      tooQuiet: t("speech.tooQuiet"),
    },
  });

  const recording = dictation.state === "recording";

  // A visible count, because the deployment stops the recording at a ceiling and an employee who
  // is cut off mid-sentence should have seen it coming.
  useEffect(() => {
    if (!recording) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      250,
    );
    return () => window.clearInterval(timer);
  }, [recording]);

  // Escape cancels and throws the audio away. The common case for wanting out is realising you
  // said the wrong thing, and the reflex for that is Escape.
  useEffect(() => {
    if (!recording) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") dictation.cancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [recording, dictation]);

  // Absent rather than disabled where the deployment has no engine, or the browser cannot record.
  // A microphone that fails when pressed teaches an employee the feature is broken.
  if (!status.enabled || !dictation.supported) return null;

  const busy = dictation.state === "transcribing";
  const remaining = Math.max(0, status.max_audio_seconds - elapsed);
  const label = recording
    ? t("speech.stopRecording", { seconds: remaining })
    : busy
      ? t("speech.transcribing")
      : t("speech.dictate");

  return (
    <div className="relative flex shrink-0 items-center">
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => (recording ? dictation.stop() : void dictation.start())}
        title={label}
        aria-label={label}
        aria-pressed={recording}
        className={cx(
          "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
          "disabled:cursor-not-allowed disabled:text-onPrimaryMuted",
          recording
            ? // Not colour alone: the glyph changes to a stop square as well, because a red
              // microphone and a grey one are the same control to anyone who cannot tell them apart.
              "bg-statusDangerSubtle text-statusDanger"
            : busy
              ? "text-accentStrong"
              : "text-onPrimaryMuted hover:bg-primarySubtle hover:text-primary",
        )}
      >
        {recording ? <StopIcon /> : <MicIcon />}
      </button>

      {/* Announced as it changes, so the state is not carried by colour or by an icon swap alone. */}
      <span role="status" aria-live="polite" className="sr-only">
        {recording ? t("speech.recordingNow", { seconds: remaining }) : ""}
      </span>

      {dictation.error ? (
        <p
          role="alert"
          className="absolute bottom-full start-0 mb-2 w-max max-w-xs rounded-lg border border-statusDanger bg-surfaceRaised px-3 py-2 text-body-sm text-statusDanger shadow-overlay"
        >
          {dictation.error}
        </p>
      ) : null}
    </div>
  );
}
